import React from 'react';
import {
  Button,
  Badge,
  Card,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  ScrollArea,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Image as ImageComponent,
} from '@city-sage/aurum-avis-labs-components';
import { useQuery } from '@tanstack/react-query';
import { useRepository } from '@city-sage/aurum-avis-labs-components';
import { useTranslation } from 'react-i18next';
import {
  MapPin,
  Calendar,
  Image as ImageIcon,
  X,
  Users,
  Clock,
  ArrowRight,
  ExternalLink,
  Heart,
  MessageCircle,
  Navigation,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Stop, Itinerary, Post, StopStateEnum } from '@city-sage/core-client';
import { Link } from '@tanstack/react-router';
import { MarkdownRenderer } from '../MarkdownRenderer';
import { useFormattedDate } from '../../lib/dateUtils';
import { Image } from '@city-sage/aurum-avis-labs-components';
import { useNavigationModalHandler } from '../../lib/hooks/useNavigation';
import { NavigationProviderModal } from '../actions/NavigationProviderModal';

export interface AggregatedStopProjection {
  location: {
    latitude: number;
    longitude: number;
    address?: {
      street?: string;
      city?: string;
      countryName?: string;
      fullAddress?: string;
    };
  };
  stopIds: string[];
  itineraryIds: string[];
}

interface StopPreviewSheetProps {
  aggregatedStop: AggregatedStopProjection | null;
  isOpen: boolean;
  onClose: () => void;
  onZoomToLocation?: (location: {
    latitude: number;
    longitude: number;
  }) => void;
}

export const StopPreviewSheet: React.FC<StopPreviewSheetProps> = ({
  aggregatedStop,
  isOpen,
  onClose,
  onZoomToLocation,
}) => {
  const { t } = useTranslation();
  const { formatDateShort } = useFormattedDate();
  const { api } = useRepository();
  const navigation = useNavigationModalHandler();

  // Fetch full stop objects from stopIds by fetching all stops from associated itineraries
  const { data: stops = [] } = useQuery({
    queryKey: [
      'stops-by-ids',
      aggregatedStop?.stopIds,
      aggregatedStop?.itineraryIds,
    ],
    queryFn: async () => {
      if (!aggregatedStop || aggregatedStop.itineraryIds.length === 0)
        return [];

      // Fetch all stops from all itineraries and filter by stopIds
      const allStopsPromises = aggregatedStop.itineraryIds.map((itineraryId) =>
        api.findAllItineraryStop({
          itineraryId,
          // TODO this is hardcoded for now, we need to fix this
          limit: 1000,
          filter: JSON.stringify({
            state: [StopStateEnum.Accepted],
          }),
        })
      );

      const allStopsArrays = await Promise.all(allStopsPromises);
      const allStops = allStopsArrays.flat();

      // Filter to only the stops we want
      return allStops.filter((stop) =>
        aggregatedStop.stopIds.includes(stop.id)
      );
    },
    enabled: !!aggregatedStop && aggregatedStop.itineraryIds.length > 0,
  });

  // Fetch full itinerary objects from itineraryIds
  const { data: fetchedItineraries = [] } = useQuery({
    queryKey: ['itineraries-by-ids', aggregatedStop?.itineraryIds],
    queryFn: async () => {
      if (!aggregatedStop || aggregatedStop.itineraryIds.length === 0)
        return [];

      // Fetch each itinerary individually
      const itineraryPromises = aggregatedStop.itineraryIds.map((itineraryId) =>
        api.findOneItinerary({ id: itineraryId })
      );

      const itineraries = await Promise.all(itineraryPromises);
      return itineraries.filter(Boolean); // Filter out any null results
    },
    enabled: !!aggregatedStop && aggregatedStop.itineraryIds.length > 0,
  });

  // Fetch posts for all stops at this location using postableId filter
  const { data: stopPosts, isLoading: isLoadingPosts } = useQuery({
    queryKey: ['posts-for-stops', aggregatedStop?.stopIds],
    queryFn: async () => {
      if (!aggregatedStop || aggregatedStop.stopIds.length === 0) return [];

      // Fetch posts for each stop using postableId filter
      const postPromises = aggregatedStop.stopIds.map((stopId) =>
        api.findAllPost({
          // TODO this is hardcoded for now, we need to fix this
          limit: 1000,
          filter: JSON.stringify({
            postableId: stopId,
            postableType: 'stop',
          }),
        })
      );

      const allPostArrays = await Promise.all(postPromises);
      return allPostArrays.flat();
    },
    enabled: !!aggregatedStop && aggregatedStop.stopIds.length > 0,
  });

  // Fetch reactions for all stops
  const { data: stopReactions } = useQuery({
    queryKey: ['reactions-for-stops', aggregatedStop?.stopIds],
    queryFn: async () => {
      if (!aggregatedStop || aggregatedStop.stopIds.length === 0) return [];

      // Fetch reactions for each stop
      const reactionPromises = aggregatedStop.stopIds.map((stopId) =>
        api.findAllReaction({
          // TODO this is hardcoded for now, we need to fix this
          limit: 1000,
          filter: JSON.stringify({
            reactableId: stopId,
            reactableType: 'stop',
          }),
        })
      );

      const allReactionArrays = await Promise.all(reactionPromises);
      return allReactionArrays.flat();
    },
    enabled: !!aggregatedStop && aggregatedStop.stopIds.length > 0,
  });

  // Get all images from stops and posts
  const allImages = React.useMemo(() => {
    if (!aggregatedStop) return [];

    const images: Array<{
      url: string;
      source: 'stop' | 'post';
      sourceId: string;
      title?: string;
    }> = [];

    // Add images from stops
    stops.forEach((stop) => {
      if (stop.imageUrl) {
        images.push({
          url: stop.imageUrl,
          source: 'stop',
          sourceId: stop.id,
          title: stop.title,
        });
      }
    });

    // Add images from stop posts
    stopPosts?.forEach((post: Post) => {
      if (post.imageUrl) {
        images.push({
          url: post.imageUrl,
          source: 'post',
          sourceId: post.id,
          title: post.text,
        });
      }
    });

    return images;
  }, [stops, stopPosts]);

  // Calculate total likes and comments from all posts and reactions
  const totalStats = React.useMemo(() => {
    const likes = stopReactions?.length || 0;
    const comments = stopPosts?.length || 0;
    return { likes, comments };
  }, [stopReactions, stopPosts]);

  // Get unique stop descriptions
  const stopDescriptions = React.useMemo(() => {
    if (!stops || stops.length === 0) return [];

    return stops
      .map((stop) => ({
        id: stop.id,
        title: stop.title,
        description: stop.highlight || stop.description,
      }))
      .filter((stop) => stop.description);
  }, [stops]);

  // Get the primary stop name for the header
  const primaryStopName = React.useMemo(() => {
    if (!stops || stops.length === 0) return null;
    
    // Get unique stop titles
    const uniqueTitles = [...new Set(stops.map(stop => stop.title))];
    
    if (uniqueTitles.length === 1) {
      return uniqueTitles[0];
    } else if (uniqueTitles.length > 1) {
      // If multiple different titles, show the first one with a count
      return `${uniqueTitles[0]} +${uniqueTitles.length - 1} more`;
    }
    
    return null;
  }, [stops]);

  // Handle navigation directions
  const handleNavigate = () => {
    if (aggregatedStop?.location) {
      const address = aggregatedStop.location.address;
      const formatAddress = () => {
        if (!address) return "Location not specified";

        const parts = [
          address.street,
          [address.city, address.countryName].filter(Boolean).join(", "),
        ].filter(Boolean);

        return parts.join(" • ");
      };

      navigation.navigate({
        latitude: aggregatedStop.location.latitude,
        longitude: aggregatedStop.location.longitude,
        title: primaryStopName || address?.city || 'Location',
        address: address?.fullAddress || formatAddress()
      });
    }
  };

  // Get traveler comments (posts with text)
  const travelerComments = React.useMemo(() => {
    return (
      stopPosts
        ?.filter((post) => post.text && post.text.trim().length > 0)
        .slice(0, 5) // Show max 5 comments
        .map((post) => ({
          id: post.id,
          text: post.text!,
          author: post.account,
          createdAt: post.createdAt,
          likesCount: 0, // We don't have individual post likes in our current structure
        })) || []
    );
  }, [stopPosts]);

  if (!aggregatedStop) return null;

  const { location } = aggregatedStop;
  const address = location.address;

  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent className="h-[85dvh] rounded-t-3xl border-0 p-0 flex flex-col">
        {/* Header - Fixed */}
        <DrawerHeader className="px-4 py-4 text-left flex-shrink-0">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <DrawerTitle className="text-xl font-bold text-gray-900 mb-2">
                {primaryStopName || address?.city || address?.fullAddress || t('common.location')}
              </DrawerTitle>

              {address && (
                <div className="flex items-start gap-2 text-sm text-gray-600">
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span className="leading-relaxed">
                    {address.fullAddress ||
                      `${address.city}, ${address.countryName}`}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Enhanced Stats */}
          <div className="flex gap-3 overflow-x-auto">
            <Badge
              variant="secondary"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 whitespace-nowrap"
            >
              <ImageIcon className="w-3 h-3" />
              <span className="font-medium">{allImages.length}</span>
            </Badge>
            <Badge
              variant="secondary"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 whitespace-nowrap"
            >
              <Calendar className="w-3 h-3" />
              <span className="font-medium">
                {fetchedItineraries.length} {t('common.itineraries')}
              </span>
            </Badge>
            <Badge
              variant="secondary"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 whitespace-nowrap"
            >
              <Users className="w-3 h-3" />
              <span className="font-medium">
                {stops.length} {t('common.stops')}
              </span>
            </Badge>
          </div>
          <div className="flex gap-3 overflow-x-auto">
            <Badge
              variant="secondary"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 whitespace-nowrap"
            >
              <Heart className="w-3 h-3 text-red-500" />
              <span className="font-medium">{totalStats.likes}</span>
            </Badge>
            <Badge
              variant="secondary"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 whitespace-nowrap"
            >
              <MessageCircle className="w-3 h-3 text-blue-500" />
              <span className="font-medium">{totalStats.comments}</span>
            </Badge>
          </div>
        </DrawerHeader>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 pb-6 space-y-6">
            {/* Photos Section - Horizontal Scroll */}
            {allImages.length > 0 && (
              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t('common.photos')} ({allImages.length})
                </h3>

                <div className="overflow-x-auto -mx-4 px-4">
                  <div
                    className="flex gap-4 pb-2"
                    style={{ width: 'max-content' }}
                  >
                    {allImages.map((image, index) => (
                      <div
                        key={`${image.source}-${image.sourceId}`}
                        className="relative w-64 h-48 rounded-2xl overflow-hidden bg-gray-100 flex-shrink-0 group cursor-pointer"
                      >
                        <Image
                          src={image.url}
                          alt={image.title || t('common.photo')}
                          className="w-full h-full object-cover"
                          imageClassName="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Stop Descriptions */}
            {stopDescriptions.length > 0 && (
              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t('itinerary.stop.aboutLocation')}
                </h3>
                <div className="space-y-4">
                  {stopDescriptions.map((stop) => (
                    <Card
                      key={stop.id}
                      className="p-4 border border-gray-200 rounded-2xl"
                    >
                      <h4 className="font-semibold text-gray-900 mb-2">
                        {stop.title}
                      </h4>
                      <MarkdownRenderer
                        content={stop.description || ''}
                        className="text-sm text-gray-700 leading-relaxed"
                      />
                    </Card>
                  ))}
                </div>
              </section>
            )}

            {/* Traveler Comments */}
            {travelerComments.length > 0 && (
              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t('itinerary.stop.travelerExperiences')} (
                  {travelerComments.length})
                </h3>
                <div className="space-y-4">
                  {travelerComments.map((comment) => (
                    <Card
                      key={comment.id}
                      className="p-4 border border-gray-200 rounded-2xl"
                    >
                      <div className="flex items-start gap-3">
                        <Avatar className="w-8 h-8">
                          <AvatarFallback className="text-xs bg-gray-100">
                            {(comment.author?.userName || 'U')
                              .charAt(0)
                              .toUpperCase()}
                          </AvatarFallback>
                        </Avatar>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-medium text-sm text-gray-900">
                              @
                              {comment.author?.userName ||
                                t('common.anonymous')}
                            </span>
                            {comment.createdAt && (
                              <span className="text-xs text-gray-500">
                                {formatDateShort(comment.createdAt)}
                              </span>
                            )}
                          </div>

                          <p className="text-sm text-gray-700 leading-relaxed mb-3">
                            {comment.text}
                          </p>

                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <div className="flex items-center gap-1">
                              <Heart className="w-3 h-3" />
                              <span>{comment.likesCount}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </section>
            )}

            {/* Itineraries Section */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t('common.itinerariesTitle')} ({fetchedItineraries.length})
              </h3>

              <div className="space-y-3">
                {fetchedItineraries.map((itinerary) => (
                  <Link
                    key={itinerary.id}
                    to="/itineraries/$itineraryId"
                    params={{ itineraryId: itinerary.id }}
                  >
                    <Card className="p-4 border border-gray-200 rounded-2xl hover:shadow-md transition-all duration-200 cursor-pointer active:scale-[0.98]">
                      <div className="flex items-center gap-4">
                        {/* Itinerary Image */}
                        <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                          {itinerary.imageUrl ? (
                            <Image
                              src={itinerary.imageUrl}
                              image={itinerary.image}
                              alt={itinerary.title}
                              className="w-full h-full"
                              imageClassName="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
                              <MapPin className="w-6 h-6 text-white" />
                            </div>
                          )}
                        </div>

                        {/* Itinerary Info */}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 truncate mb-1">
                            {itinerary.title}
                          </h4>
                          <p className="text-sm text-gray-600 mb-2">
                            {t('common.by')} @{itinerary.account.userName}
                          </p>

                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <div className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              <span>{itinerary.daysCount}d</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              <span>
                                {itinerary.stopsCount} {t('common.stops')}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Arrow */}
                        <div className="flex-shrink-0">
                          <ArrowRight className="w-4 h-4 text-gray-400" />
                        </div>
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            </section>


          </div>
        </div>

        {/* Drawer Footer - Fixed at bottom */}
        <div className="flex-shrink-0 p-4 bg-white border-t border-gray-100">
          <div className="space-y-3">
            {/* Get Directions Button */}
            <Button
              onClick={handleNavigate}
              size="lg"
              className="w-full"
            >
              <Navigation className="w-4 h-4 mr-2" />
              {t('itinerary.stopPage.getDirections')}
            </Button>

            {/* View on Map Button */}
            {onZoomToLocation && (
              <Button
                onClick={() => onZoomToLocation(location)}
                variant="outline"
                size="lg"
                className="w-full"
              >
                <MapPin className="w-4 h-4 mr-2" />
                {t('common.viewOnMap')}
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </DrawerContent>

      {/* Navigation Provider Modal */}
      <NavigationProviderModal
        isOpen={navigation.isModalOpen}
        onClose={() => navigation.setIsModalOpen(false)}
        onSelect={navigation.handleProviderChoice}
        destinationName={primaryStopName || address?.city || 'Location'}
      />
    </Drawer>
  );
};
